<template>
  <div>
    <OverviewComponent
      icon="mdi-hammer-wrench"
      :subtitle="subtitle"
      :headers="headers"
      :data="data"
      :loading="loading"
      :totalPages="totalPages"
      :totalItemCount="totalItemCount"
      underRightKey="responsible"
      deadlineKey="timeLimit"
      :defaultSortBy="defaultSortBy"
      :defaultSortDesc="defaultSortDesc"
      @setHeader="setHeader"
      @fetchData="fetchData"
      @setColor="setColor"
      @click="click"
    >
      <template v-slot:title="{ item }">
        {{ (item.id ? item.id : "") + (item.title ? ` - ${item.title}` : "") }}
      </template>

      <template v-slot:chips="{ item }">
        <div v-for="(chip, index) in getChips(item)" :key="index">
          <v-chip class="mr-2" :color="chip.color" small>
            {{ chip.title }}
          </v-chip>
        </div>
      </template>

      <template v-slot:underLeft="{ item }">
        {{ item.projectName }}
        {{ item.projectName && item.apartmentKey ? "-" : "" }}
        {{ item.apartmentKey }}
      </template>

      <!-- Custom filter -->

      <template v-slot:filter.projectName="{ head, isTable }">
        <v-autocomplete
          v-if="!head.removeFilter"
          :class="{ 'mb-0': isTable }"
          v-model="head.filterValues"
          :items="isTrysilhus ? projects : projects"
          :filled="!isTable"
          :outlined="isTable"
          :dense="isTable"
          :label="isTable ? '' : head.text"
          :placeholder="isTable ? 'Søk...' : ''"
          item-text="name"
          item-value="id"
          clearable
          :disabled="head.disabled"
          hide-details="auto"
          menu-props="auto"
          multiple
        >
          <template v-slot:selection="{ item, index }">
            <span v-if="index === 0">{{ item.name }}</span>
            <span v-if="index === 1" class="grey--text text-caption">
              &nbsp;(+{{ head.filterValues.length - 1 }} andre)
            </span>
          </template>
        </v-autocomplete>
      </template>

      <template v-slot:filter.responsible="{ head, isTable }">
        <v-autocomplete
          v-if="!head.removeFilter"
          :class="{ 'mb-0': isTable }"
          v-model="head.filterValues"
          :items="isTrysilhus ? contactPersons : contactPersonContact"
          :filled="!isTable"
          :outlined="isTable"
          :dense="isTable"
          :label="isTable ? '' : 'Ansvarlig'"
          :placeholder="isTable ? 'Søk...' : ''"
          item-text="name"
          item-value="id"
          clearable
          :disabled="head.disabled"
          hide-details="auto"
          multiple
        >
          <template v-slot:selection="{ item, disabled, index }">
            <div
              v-if="typeof head.filterValues === 'object'"
              class="v-select__selection v-select__selection--comma"
            >
              {{
                head.filterValues && head.filterValues.length === 1
                  ? item.name
                  : index === 1
                  ? `${head.filterValues.length} valgt`
                  : ""
              }}
            </div>
            <div
              v-else
              class="v-select__selection v-select__selection--comma"
              :class="{ 'v-select__selection--disabled': disabled }"
            >
              {{ item.name }}
            </div>
          </template>

          <template v-slot:item="{ active, item, attrs, on }">
            <v-list-item
              v-on="on"
              v-bind="attrs"
              #default="{ active }"
              :disabled="
                !isTrysilhus &&
                attrs.inputValue &&
                head.filterValues.length === 1
              "
            >
              <v-list-item-action>
                <v-checkbox :input-value="active"></v-checkbox>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>
                  {{ item.name }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </template>
        </v-autocomplete>
      </template>

      <template v-slot:filter.createdBy="{ head, isTable }">
        <v-autocomplete
          :class="{ 'mb-0': isTable }"
          v-model="head.filterValues"
          :items="users"
          :filled="!isTable"
          :outlined="isTable"
          :dense="isTable"
          :label="isTable ? '' : 'Opprettet av'"
          :placeholder="isTable ? 'Søk...' : ''"
          item-text="name"
          item-value="name"
          clearable
          hide-details="auto"
        ></v-autocomplete>
      </template>
      <template v-slot:filter.aberrationSeverity="{ head, isTable }">
        <v-select
          :class="{ 'mb-0': isTable }"
          v-model="head.filterValues"
          :items="actionSeverities"
          :filled="!isTable"
          :outlined="isTable"
          :dense="isTable"
          :label="isTable ? '' : 'Alvorlighetsgrad'"
          :placeholder="isTable ? 'Velg' : ''"
          item-text="name"
          item-value="name"
          clearable
          hide-details="auto"
        ></v-select>
      </template>

      <template v-slot:filter.statusText="{ head, isTable }">
        <v-select
          :class="{ 'mb-0': isTable }"
          v-model="head.filterValues"
          :items="actionStatus"
          :filled="!isTable"
          :outlined="isTable"
          :dense="isTable"
          :label="isTable ? '' : 'Status'"
          :placeholder="isTable ? 'Velg' : ''"
          item-text="name"
          item-value="id"
          clearable
          hide-details="auto"
        ></v-select>
      </template>
    </OverviewComponent>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import { mapGetters } from "vuex";
import { HeaderObject } from "@/types/tableInterface";
import aberrationApi from "@/api/aberrationApi";

import OverviewComponent from "@/components/shared/overview/OverviewComponent.vue";

export default Vue.extend({
  data: () => ({
    data: [] as Array<unknown>,
    headers: [] as Array<HeaderObject>,
    loading: true,
    totalPages: 0,
    totalItemCount: -1,
    defaultSortBy: ["createdDate"],
    defaultSortDesc: [true],
    contactPersons: [],
    users: [],
    actionSeverities: [],
    actionStatus: [],
    contactPersonContact: [],
    projects: [],
  }),
  mounted() {
    this.getFilterData();
  },

  methods: {
    async fetchData({ data, bottomOfPage }) {
      this.loading = true;

      await aberrationApi
        .getActions(data)
        .then((response) => {
          if (bottomOfPage) {
            this.data = [...this.data, ...response.items];
          } else {
            this.data = response.items;
          }

          this.totalPages = response.totalPages;
          this.totalItemCount = response.totalItemCount;
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    getFilterData() {
      aberrationApi.getActionsFilteroptions().then((response) => {
        this.contactPersons = response.contactPersons;
        this.users = response.users;
        this.actionSeverities = response.actionSeverities;
        this.actionStatus = response.actionStatus;
        this.contactPersonContact = response.contactPersonContact;
        this.projects = response.projects;
      });
    },
    clickCard(item: any) {
      this.$router.push({ name: "Action", params: { id: item.id } });
    },
    getChips(item: any) {
      var data = [
        {
          title: item.statusText,
          color: item.color,
        },
      ];
      if (item.aberrationSeverity) {
        data.push({
          title: item.aberrationSeverity,
          color: "",
        });
      }

      return data;
    },
    setColor(item) {
      this.$set(item, "color", item.completedDate ? "success" : "warning");
    },
    setHeader() {
      this.headers = [
        {
          text: "Id",
          value: "id",
          type: "string",
          show: true,
          filterValues: "",
          filterClause: 0,
          filterName: "id",
          sortIndex: 0,
        },
        {
          text: "Tittel",
          value: "title",
          type: "string",
          show: true,
          filterValues: "",
          filterClause: 0,
          filterName: "title",
          sortIndex: 1,
        },
        {
          text: "Status",
          value: "statusText",
          type: "string",
          show: true,
          filterValues: 1,
          filterClause: 0,
          filterName: "actionStatus",
          sortIndex: 2,
        },
        {
          text: "Beskrivelse",
          value: "description",
          type: "string",
          show: false,
          filterValues: "",
          filterClause: 0,
          filterName: "description",
          removeFilter: true,
        },
        {
          text: "Alvorlighetsgrad",
          value: "aberrationSeverity",
          type: "string",
          show: true,
          filterValues: "",
          filterClause: 3,
          filterName: "aberrationSeverity",
          sortIndex: 3,
        },
        {
          text: "Frist",
          value: "timeLimit",
          type: "date",
          show: true,
          filterValues: null,
          filterClause: 0,
          filterName: "timeLimit",
          sortIndex: 4,
          dateType: "DD.MM.YYYY",
        },
        {
          text: "Prosjekt",
          value: "projectName",
          type: "string",
          show: true,
          filterValues: "",
          filterClause: 0,
          filterName: "projTable",
          sortIndex: 5,
        },
        {
          text: "Lelighetsnummer",
          value: "apartmentKey",
          type: "string",
          show: true,
          filterValues: "",
          filterClause: 0,
          filterName: "apartmentKey",
          sortIndex: 5,
        },
        {
          text: "Ansvarlig",
          value: "responsible",
          type: "string",
          show: true,
          filterValues: this.showAllActions
            ? !this.isTrysilhus
              ? this.contactPersonContactIds
              : ""
            : this.profile.id,
          filterClause: 0,
          filterName: "responsibleId",
          disabled: this.showAllActions ? false : true,
          isAssignedTo: true,
        },
        // {
        //   text: "Ansvarlig",
        //   value: "responsible",
        //   type: "string",
        //   show: false,
        //   filterValues: this.showAllActions
        //     ? !this.isTrysilhus
        //       ? this.contactPersonContactIds
        //       : ""
        //     : this.profile.id,
        //   filterClause: 0,
        //   filterName: "responsibleId",
        //   removeFilter: true,
        // },
        {
          text: "Opprettet av",
          value: "createdBy",
          type: "string",
          show: true,
          filterValues: "",
          filterClause: 0,
          filterName: "createdBy",
          sortIndex: 7,
        },
        {
          text: "Opprettet",
          value: "createdDate",
          type: "date",
          show: true,
          filterValues: null,
          filterClause: 0,
          filterName: "createdDate",
          dateType: "DD.MM.YYYY",
        },
      ];
    },
    click(item) {
      this.$router.push({ name: "Action", params: { id: item.id } });
    },
  },

  computed: {
    ...mapGetters({
      isTrysilhus: "shared/isTrysilhus",
      profile: "shared/profile",
      showAll: "tables/showAll",
      contactPersonContactIds: "shared/contactPersonContactIds",
    }),
    showAllActions(): boolean {
      return this.showAll.value;
    },
    subtitle(): string {
      return this.showAllActions && this.isTrysilhus
        ? "Alle tiltak"
        : "Dine tiltak";
    },
  },
  components: {
    OverviewComponent,
  },
});
</script>
